import React, { useRef, useState, useEffect } from "react";
import AudioPlayer from "./AudioPlayerV2";

import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

import { logEvent } from "firebase/analytics";
import { analytics } from "../../firebase";

import "./StoryContent.css";

export default function StoryContent({
  story,
  tokens,
  sectionSentences = [],
  sectionIndex,
  totalSections,
  showTranslations,
  onToggleTranslations,
  selectedWord,
  onWordClick,
  savedWords = [],
  addedWords = [],
  isFavorite,
  onFavoriteClick,
  onPrevSection,
  onNextSection,
  storySections,
}) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [currentSentenceId, setCurrentSentenceId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const storyStartedLogged = useRef(false);

  const trackStoryStarted = () => {
    if (storyStartedLogged.current || !analytics || !story) return;
    storyStartedLogged.current = true;
    logEvent(analytics, "story_started", { story_id: story.id });
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = (e.target.value / 100) * duration;
    audio.currentTime = newTime;
    setProgress(e.target.value);
  };

  const handleSpeedChange = (e) => {
    const newRate = parseFloat(e.target.value);
    setPlaybackRate(newRate);
    if (audioRef.current) audioRef.current.playbackRate = newRate;
  };

  useEffect(() => {
    if (!story) return;
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => setDuration(audio.duration || 0);
    const onTimeUpdate = () => {
      const current = audio.currentTime;
      setCurrentTime(current);
      if (duration > 0) setProgress((current / duration) * 100);

      const activeSentence = story.sentences.find((s) => current >= s.audioStart && current < s.audioEnd);
      setCurrentSentenceId(activeSentence ? activeSentence.id : null);
    };

    const onEnded = () => {
      setIsPlaying(false);
      setCurrentSentenceId(null);
      setProgress(0);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
    };
  }, [story, duration]);

  useEffect(() => {
    if (!currentSentenceId) return;
    const el = document.getElementById(`sentence-${currentSentenceId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [currentSentenceId]);

  useEffect(() => {
    if (!story) return;
    const timer = setTimeout(() => {
      trackStoryStarted();
    }, 10000);
  
    return () => clearTimeout(timer);
  }, [story]);

  useEffect(() => {
    if (!currentSentenceId || !isPlaying) return;
    if (!storySections || storySections.length === 0) return;
  
    const isInCurrentSection = (storySections[sectionIndex]?.sentences || []).some(
      (s) => s.id === currentSentenceId
    );
  
    if (isInCurrentSection) return; // already on the right page, do nothing
  
    const targetIndex = storySections.findIndex((section) =>
      section.sentences.some((s) => s.id === currentSentenceId)
    );
  
    // only advance forward, and only if actually found
    if (targetIndex > sectionIndex) {
      onNextSection?.();
    } else if (targetIndex < sectionIndex) {
      onPrevSection?.();
    }
  }, [currentSentenceId]);

  const handleSkipBack = () => {
    if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
  };
  
  const handleSkipForward = () => {
    if (audioRef.current) audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
  };

  console.log("sectionSentences", sectionSentences);
  console.log("currentSentenceId", currentSentenceId);


  if (!story) return null;

  return (
    <div className="story-text-section">
      <AudioPlayer
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        progress={progress}
        playbackRate={playbackRate}
        onPlayPause={handlePlayPause}
        onSeek={handleSeek}
        onSpeedChange={handleSpeedChange}
        showTranslations={showTranslations}
        onToggleTranslations={onToggleTranslations}
        isFavorite={isFavorite}
        onFavoriteClick={onFavoriteClick}
        onPrevSection={onPrevSection}
        onNextSection={onNextSection}
        onSkipBack={handleSkipBack}
        onSkipForward={handleSkipForward}
      />

      <div className="story-progress-sticky">
        <div className="story-session-progress-bars">
          {Array.from({ length: totalSections }).map((_, i) => (
            <div
              key={i}
              className={
                i < sectionIndex
                  ? "session-bar done"
                  : i === sectionIndex
                  ? "session-bar active"
                  : "session-bar"
              }
            />
          ))}
        </div>
      </div>

      <div className="story-text">
        {Object.entries(
          (sectionSentences || []).reduce((acc, s) => {
            const p = s.paragraph ?? 1;
            (acc[p] ||= []).push(s);
            return acc;
          }, {})
        ).map(([pNum, sentences]) => (
          <div key={pNum} className="story-paragraph">
            {sentences.map((s) => {
              const isLine = s.display === "line"; // <-- NEW (set in story.json)
              const globalIndex = sectionSentences.findIndex(sentence => sentence.id === s.id);
              const currentIndex = sectionSentences.findIndex(s => s.id === currentSentenceId);
              const distance = Math.abs(globalIndex - currentIndex);
              const sameParagraph = sectionSentences[globalIndex]?.paragraph === sectionSentences[currentIndex]?.paragraph;

              const opacity = !isPlaying || currentSentenceId === null ? 1
              : sameParagraph ? 1
              : 0.5;            

              return (
                <React.Fragment key={s.id}>
                  <span
                    id={`sentence-${s.id}`}
                    style={{ opacity, transition: 'opacity 0.4s ease' }}
                    className={[
                      "sentence-text",
                      isLine ? "sentence-text--line" : "",
                      currentSentenceId === s.id ? "highlighted-sentence" : "",
                    ].join(" ")}
                  >
                    {tokens?.sentences?.[s.id]
                      ? (
                          <>
                            {s.text.split(/(\s+)/).map((chunk, idx) => {
                              const clean = chunk
                                .replace(/[.,!?;:()"“”„]/g, "")
                                .trim()
                                .toLowerCase();
                              const token = tokens.sentences[s.id].find(
                                (t) => t.word.toLowerCase() === clean
                              );
                              if (token && clean !== "") {
                                return (
                                  <span
                                    key={idx}
                                    onClick={(e) => onWordClick(token, e)}
                                    className={[
                                      selectedWord === token.word ? "selected-word" : "",
                                      savedWords.includes(token.word.toLowerCase()) ||
                                      addedWords.includes(token.word.toLowerCase()) ||
                                      savedWords.includes(token.lemma?.toLowerCase()) ||
                                      addedWords.includes(token.lemma?.toLowerCase())
                                        ? "saved-word"
                                        : "",
                                    ].join(" ")}
                                  >
                                    {chunk}
                                  </span>
                                );
                              }
                              return chunk;
                            })}

                          </>
                        )
                      : s.text + " "}
                  </span>

                  {showTranslations && (
                    <p
                      style={{ opacity, transition: 'opacity 0.4s ease' }}
                      className={[
                        "sentence-translation",
                        isLine ? "sentence-translation--line" : "",
                      ].join(" ")}
                    >
                      {s.translation}
                    </p>
                  )}

                </React.Fragment>
              );
            })}
            
          </div>
          
        ))}

        {/* Backup navigation at end of section */}
        <div className="story-section-nav">
          <button
            className="story-section-nav-btn"
            onClick={onPrevSection}
            disabled={!onPrevSection}
          >
            <IoIosArrowBack size={26} />
          </button>
          <button
            className="story-section-nav-btn"
            onClick={onNextSection}
            disabled={!onNextSection}
          >
            <IoIosArrowForward size={26} />
          </button>
        </div>

      </div>


      <audio ref={audioRef} src={story.audio} preload="auto" />
    </div>
  );
}
